import { supabase } from "@/integrations/supabase/client";

/**
 * Logger de transições de permissionState/status do BackgroundLocation.
 *
 * - Sempre loga no console (com prefixo) para inspeção em devtools.
 * - Persiste em `client_error_logs` (severity="info") APENAS quando o
 *   usuário concedeu consent_geolocation no perfil. Sem consentimento,
 *   apenas o console é usado.
 * - Deduplica transições idênticas consecutivas (mesma chave + mesmo from→to)
 *   para evitar flood quando o estado oscila no mesmo render.
 */

type TransitionKind = "permissionState" | "status";

interface TransitionPayload {
  kind: TransitionKind;
  from: string;
  to: string;
  context?: Record<string, unknown>;
}

export interface TransitionLogEntry {
  id: number;
  at: number;
  kind: TransitionKind;
  from: string;
  to: string;
  context?: Record<string, unknown>;
}

const PREFIX = "[bg-location]";
const lastByKind = new Map<TransitionKind, string>();

// ---- Ring buffer in-memory dos últimos 50 eventos ----
const HISTORY_LIMIT = 50;
const history: TransitionLogEntry[] = [];
let historySeq = 0;
type Listener = (entries: readonly TransitionLogEntry[]) => void;
const listeners = new Set<Listener>();

export function getLocationDebugHistory(): readonly TransitionLogEntry[] {
  return history;
}

export function subscribeLocationDebugHistory(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function clearLocationDebugHistory(): void {
  history.length = 0;
  listeners.forEach((fn) => fn(history));
}

function pushHistory(entry: Omit<TransitionLogEntry, "id" | "at">) {
  history.push({ ...entry, id: ++historySeq, at: Date.now() });
  if (history.length > HISTORY_LIMIT) history.splice(0, history.length - HISTORY_LIMIT);
  listeners.forEach((fn) => fn(history));
}
const consentCache = new Map<string, { value: boolean; at: number }>();
const CONSENT_TTL_MS = 60_000;

export const LOCATION_DEBUG_PERSIST_KEY = "zappy.location-debug.persist";

/**
 * Persistência em `client_error_logs` é ferramenta de diagnóstico, não
 * telemetria de produto: em produção fica DESLIGADA por padrão (opt-in
 * explícito no painel de debug) para não poluir a base de erros. Em
 * desenvolvimento continua ligada por padrão.
 */
export function isLocationDebugPersistEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const defaultEnabled = Boolean(import.meta.env?.DEV);
  try {
    const stored = window.localStorage.getItem(LOCATION_DEBUG_PERSIST_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
    return defaultEnabled;
  } catch {
    return defaultEnabled;
  }
}

export function setLocationDebugPersistEnabled(enabled: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCATION_DEBUG_PERSIST_KEY, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export async function getLocationDebugConsent(userId: string): Promise<boolean> {
  return hasGeoConsent(userId);
}

async function hasGeoConsent(userId: string): Promise<boolean> {
  const cached = consentCache.get(userId);
  if (cached && Date.now() - cached.at < CONSENT_TTL_MS) return cached.value;
  try {
    const { data } = await supabase
      .from("profiles")
      .select("consent_geolocation")
      .eq("id", userId)
      .maybeSingle();
    const value = Boolean(data?.consent_geolocation);
    consentCache.set(userId, { value, at: Date.now() });
    return value;
  } catch {
    return false;
  }
}

export async function logLocationTransition(
  userId: string | undefined,
  payload: TransitionPayload
): Promise<void> {
  const { kind, from, to, context } = payload;
  if (from === to) return;
  const dedupeKey = `${from}->${to}`;
  if (lastByKind.get(kind) === dedupeKey) return;
  lastByKind.set(kind, dedupeKey);

  // Ruído de diagnóstico: mantém no nível debug para não poluir o console.
  // eslint-disable-next-line no-console
  console.debug(`${PREFIX} ${kind}: ${from} → ${to}`, context ?? {});
  pushHistory({ kind, from, to, context });

  if (!userId) return;
  if (!isLocationDebugPersistEnabled()) return;
  try {
    const allowed = await hasGeoConsent(userId);
    if (!allowed) return;
    await supabase.from("client_error_logs").insert({
      user_id: userId,
      severity: "info",
      message: `${PREFIX} ${kind}: ${from} → ${to}`,
      route: typeof window !== "undefined" ? window.location.pathname : null,
      url: typeof window !== "undefined" ? window.location.href : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      metadata: { kind, from, to, context: context ?? {} },
    } as never);
  } catch {
    /* swallow - debug only */
  }
}

/** Limpa cache de consentimento (ex.: ao revogar). */
export function resetLocationDebugConsentCache(userId?: string) {
  if (userId) consentCache.delete(userId);
  else consentCache.clear();
}
